/**
 * Keybinding subsystem contract (架构方案 §5.9 / 见 03).
 *
 * A keybinding never carries behaviour: it names a command. That is what keeps
 * one action reachable from a chord, the command list, a menu and a global OS
 * shortcut without four implementations — and it is why this file defines an
 * identity, an override record and a registration status, but no handler.
 *
 * Its own channel rather than methods on `IConfigurationService`: a chord table
 * is a structured record, while `SettingType` is `boolean | string | number |
 * enum`, so a setting could only ever hold a stringified copy the settings UI
 * would then have to special-case (same reasoning as `SEARCH_RECENT_CHANNEL`).
 */

import type { ModuleRuntime } from './index';

/** Cross-process channel for user keybinding overrides (trusted renderer only). */
export const KEYBINDING_CHANNEL = 'keybinding';

/**
 * Where a resolved binding came from, which is also its precedence
 * (`builtin < module < user`).
 *
 * VS Code spends five weights here (`EditorCore` … `ExternalExtension`) because
 * it has an editor core competing with the workbench; we have no such layer, so
 * the source *is* the priority and there is no free-form weight to drift.
 */
export type KeybindingSource = 'builtin' | 'module' | 'user';

/** Precedence per {@link KeybindingSource}; higher wins. */
export const KEYBINDING_SOURCE_RANK: Readonly<
  Record<KeybindingSource, number>
> = {
  builtin: 0,
  module: 100,
  user: 200,
};

/**
 * Where a chord is listened for.
 *
 * `window` is an in-page `keydown`: it fires only while a window of this app has
 * focus. `global` is an OS-level grab (desktop only): it fires with the app in
 * the background, which is why it is permission-gated and why the OS may refuse
 * it outright when another application already owns the chord.
 */
export type KeybindingScope = 'window' | 'global';

/**
 * Stable identity of a **declared** binding — the anchor a user override points
 * at, and the row key in the shortcuts settings page.
 *
 * Built from the declaration rather than assigned: an id has to survive a
 * restart, a module update and a re-resolve, and nothing in this system has a
 * place to persist a generated one. The default `key` is part of the identity
 * because a command may declare several chords; the consequence — a module that
 * changes its own default chord orphans the user's override for it — is the
 * correct outcome, since the thing the user rebound no longer exists.
 */
export function keybindingId(binding: {
  moduleId: string;
  command: string;
  key: string;
  when?: string;
}): string {
  return [
    binding.moduleId,
    binding.command,
    binding.key.toLowerCase().replace(/\s+/g, ''),
    binding.when ?? '',
  ].join('|');
}

/**
 * One user edit of a declared binding, keyed by {@link keybindingId}.
 *
 * Two edits, one record: `key` re-points the chord, `disabled` removes the
 * default outright (VS Code spells the latter `-command` in `keybindings.json`).
 * Both are needed — "I want Ctrl+K to do nothing" is not expressible by
 * rebinding it to something else.
 */
export interface KeybindingOverride {
  /** The declared binding this edits; see {@link keybindingId}. */
  readonly id: string;
  /** Replacement chord. Absent when the user only disabled the default. */
  readonly key?: string;
  /** The user removed this binding; it resolves to nothing at all. */
  readonly disabled?: boolean;
}

/** Why the OS refused a global chord. */
export type GlobalShortcutFailure =
  /**
   * Another application owns it. `globalShortcut.register` reports this by
   * returning `false` — it does not throw — so reading that return value is the
   * only way the failure can surface at all.
   */
  | 'taken'
  /** The chord has no Electron accelerator spelling (e.g. a bare modifier). */
  | 'invalid';

/** Outcome of registering one `scope: 'global'` binding (desktop only). */
export interface GlobalShortcutStatus {
  /** {@link keybindingId} of the binding this outcome belongs to. */
  readonly id: string;
  /** The Electron accelerator that was attempted. */
  readonly accelerator: string;
  readonly registered: boolean;
  /** Absent when `registered`. */
  readonly reason?: GlobalShortcutFailure;
}

/**
 * A chord in the form both processes can compare: modifier state plus one
 * **physical** key (`KeyboardEvent.code`, which `Electron.Input` also carries).
 *
 * Physical rather than the produced character for the reason spelled out in the
 * renderer's `chord.ts`: a character-keyed chord moves with the keyboard layout
 * and is unbindable outright where the character is a dead key.
 */
export interface ShortcutChord {
  readonly ctrl: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
  readonly code: string;
}

/**
 * A resolved global binding as handed to the OS layer: identity, the chord to
 * grab, and what to run — already merged with the user's overrides, because the
 * main process must not hold a second opinion about which chord is in effect.
 */
export interface ResolvedGlobalBinding {
  readonly id: string;
  /** Electron accelerator spelling (`Control+Shift+P`). */
  readonly accelerator: string;
  readonly command: string;
  readonly args?: readonly unknown[];
}

/**
 * Built-in commands the **main process** executes, listed here because that is
 * the one fact the renderer's table and the main process must agree on.
 *
 * They are ordinary keybindings — declared once, shown in the shortcuts page,
 * rebindable — that simply cannot run in the renderer: toggling a window's
 * full-screen state and respawning the extension host are main-process powers.
 * Main also has to *see* the key before the page does, since the chord must
 * still work while a `WebContentsView` or a cross-origin iframe holds focus, and
 * a `keydown` in those never reaches the workbench document.
 */
export const MAIN_SHORTCUT_COMMAND = {
  toggleFullScreen: 'workbench.toggleFullScreen',
  /** Dev-only: apply rebuilt modules without restarting Electron (见 01). */
  reloadWindow: 'workbench.reloadWindow',
  /** Dev-only: the cache-bypassing variant of {@link reloadWindow}. */
  reloadWindowHard: 'workbench.reloadWindowHard',
} as const;

/** One main-executed window chord, as published by the renderer. */
export interface MainWindowShortcut {
  readonly id: string;
  readonly command: string;
  readonly chord: ShortcutChord;
}

/**
 * Everything the renderer's resolved table hands to the processes below it.
 *
 * One payload rather than two calls: both halves come from the same merge, and
 * publishing them separately would let a rebind land in one and not the other.
 */
export interface PublishedShortcuts {
  /** OS-level grabs (desktop only). */
  readonly global: readonly ResolvedGlobalBinding[];
  /** Window chords the main process owns; see {@link MAIN_SHORTCUT_COMMAND}. */
  readonly mainWindow: readonly MainWindowShortcut[];
}

/**
 * EventBus event carrying the full override list after any edit.
 *
 * The whole list, not a delta: a receiver that missed one message must not end
 * up with a table that silently disagrees with the host's.
 *
 * Deliberately **not** in `PLATFORM_PUBLIC_EVENTS` (same reasoning as
 * `WHEN_CONTEXT_CHANGED_EVENT`): the payload names which modules are installed
 * and which of their commands the user reached for.
 */
export const KEYBINDINGS_CHANGED_EVENT = 'platform.keybindings.changed';

/** Payload of {@link KEYBINDINGS_CHANGED_EVENT}. */
export interface KeybindingsChanged {
  readonly overrides: readonly KeybindingOverride[];
}

/**
 * EventBus event carrying every global registration outcome after a
 * re-register.
 *
 * Separate from {@link KEYBINDINGS_CHANGED_EVENT} because it travels the other
 * way and lands later: the host writes an override, the main process then tries
 * the OS grab, and only then is it known whether the chord the user just chose
 * actually works.
 */
export const GLOBAL_SHORTCUT_STATUS_EVENT = 'platform.keybindings.globalStatus';

/** Payload of {@link GLOBAL_SHORTCUT_STATUS_EVENT}. */
export interface GlobalShortcutStatusChanged {
  readonly statuses: readonly GlobalShortcutStatus[];
}

/**
 * Trusted-renderer RPC for the user's keybinding edits (架构方案 §5.9).
 *
 * The host holds them because they are cross-window state: a rebind made in the
 * settings page must reach the detached window that has the same chord
 * installed, and the desktop's OS-level grabs are registered once for the whole
 * app rather than once per renderer.
 */
export interface IKeybindingService {
  getOverrides(): Promise<readonly KeybindingOverride[]>;
  /** Write one edit, replacing any previous edit of the same binding. */
  setOverride(override: KeybindingOverride): Promise<void>;
  /** Drop the edit for one binding, restoring its declared chord. */
  reset(id: string): Promise<void>;
  /** Drop every edit. */
  resetAll(): Promise<void>;
  /**
   * Publish the resolved chords the processes below the renderer have to know
   * about, and report back what the OS actually accepted.
   *
   * Called by the renderer that resolved them (it is the side holding the
   * platform's built-in table); the host keeps the latest payload and forwards
   * it. Returns the outcomes so the caller that just rebound a chord learns
   * immediately that it was refused, instead of finding out by pressing it.
   */
  publishShortcuts(
    shortcuts: PublishedShortcuts,
  ): Promise<readonly GlobalShortcutStatus[]>;
  /** Last known registration outcomes; empty on runtimes without OS grabs. */
  getGlobalStatus(): Promise<readonly GlobalShortcutStatus[]>;
}

/**
 * Runtimes an OS-level grab exists on. Web has no equivalent at all: the closest
 * a page can get is the Keyboard Lock API, which only applies inside a
 * JavaScript-initiated fullscreen and is not Baseline (见 18) — a workbench is
 * not going to hold the screen hostage to own a chord.
 */
export const GLOBAL_SHORTCUT_RUNTIMES: readonly ModuleRuntime[] = ['desktop'];
